// React bootstrap table next =>
// DOCS: https://react-bootstrap-table.github.io/react-bootstrap-table2/docs/
// STORYBOOK: https://react-bootstrap-table.github.io/react-bootstrap-table2/storybook/index.html
import React, { useEffect, useMemo } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, {
  PaginationProvider,
} from "react-bootstrap-table2-paginator";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../../_redux/redeems/redeemsActions";
import {
  getSelectRow,
  getHandlerTableChange,
  NoRecordsFoundMessage,
  PleaseWaitMessage,
  sortCaret,
  headerSortingClasses,
} from "../../../../../../_metronic/_helpers";
import * as uiHelpers from "../RedeemsUIHelpers";
import * as columnFormatters from "./column-formatters";
import { Pagination } from "../../../../../../_metronic/_partials/controls";
import { useRedeemsUIContext } from "../RedeemsUIContext";

export function RedeemsTable() {
  // Redeems UI Context
  const redeemsUIContext = useRedeemsUIContext();
  const redeemsUIProps = useMemo(() => {
    return {
      ids: redeemsUIContext.ids,
      setIds: redeemsUIContext.setIds,
      queryParams: redeemsUIContext.queryParams,
      setQueryParams: redeemsUIContext.setQueryParams,
      openEditRedeemDialog: redeemsUIContext.openEditRedeemDialog,
      openDeleteRedeemDialog: redeemsUIContext.openDeleteRedeemDialog,
      openFetchRedeemsDetailsDialog: redeemsUIContext.openFetchRedeemsDetailsDialog,
      openPhoneVerificationDialog: redeemsUIContext.openPhoneVerificationDialog,
      openCryptoPaymentPage: redeemsUIContext.openCryptoPaymentPage,
      openIdentityVerificationDialog: redeemsUIContext.openIdentityVerificationDialog,
    };
  }, [redeemsUIContext]);

  // Getting curret state of redeems list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.redeems }),
    shallowEqual
  );
  const { totalCount, entities, listLoading } = currentState;

  // Redeems Redux state
  const dispatch = useDispatch();
  useEffect(() => {
    // clear selections list
    redeemsUIProps.setIds([]);
    // server call by queryParams
    dispatch(actions.fetchRedeems(redeemsUIProps.queryParams));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redeemsUIProps.queryParams, dispatch]);
  // Table columns
  const columns = [
    {
      dataField: "_id",
      text: "ID",
      sort: true,

      sortCaret: sortCaret,
      headerSortingClasses,
    },
    {
      dataField: "redeemUser",
      text: "USER",
      sort: false,
      sortCaret: sortCaret,
      headerSortingClasses,
      style: {
        maxWidth: "250px",
      },
    },
    {
      dataField: "redeemEmail",
      text: "Receiver EMAIL",
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
      style: {
        maxWidth: "250px",
      },
    },

    {
      dataField: "rewardName",
      text: "REQUESTED REWARD",
      sort: false,
      sortCaret: sortCaret,
      headerSortingClasses,
    },
    {
      dataField: "rewardAmount",
      text: "AMOUNT",
      sort: false,
      sortCaret: sortCaret,
      headerSortingClasses,
    },
    {
      dataField: "rewardCost",
      text: "POINTS",
      sort: false,
      sortCaret: sortCaret,
    },
    {
      dataField: "code",
      text: "GIFT CARD CODE",
      sort: false,
      sortCaret: sortCaret,
      headerSortingClasses,
    },
    {
      dataField: "rewardExtraInfo",
      text: "EXTRA INFO",
      sort: false,
      sortCaret: sortCaret,
      style: {
        maxWidth: "250px",
      },
    },
    {
      dataField: "redeemDate",
      text: "CREATED AT",
      sort: true,
      sortCaret: sortCaret,
      formatter: columnFormatters.TypeColumnFormatter,
      style: {
        minWidth: "120px",
      },
    },
    {
      dataField: "status",
      text: "STATUS",
      sort: false,
      sortCaret: sortCaret,
      headerSortingClasses,
      formatter: columnFormatters.StatusColumnFormatter,
      style: {
        maxWidth: "80px",
      },
    },  
    {
      dataField: "action",
      text: "Actions",
      formatter: columnFormatters.ActionsColumnFormatter,
      formatExtraData: {
        openEditRedeemDialog: redeemsUIProps.openEditRedeemDialog,
        openFetchRedeemsDetailsDialog: redeemsUIProps.openFetchRedeemsDetailsDialog,
        openDeleteRedeemDialog: redeemsUIProps.openDeleteRedeemDialog,
        openPhoneVerificationDialog: redeemsUIProps.openPhoneVerificationDialog,
        openCryptoPaymentPage: redeemsUIProps.openCryptoPaymentPage,
        openIdentityVerificationDialog: redeemsUIProps.openIdentityVerificationDialog
      },
      classes: "text-right pr-0",
      headerClasses: "text-right pr-3",
      style: {
        minWidth: "150px",
      },
    },
  ];
  // Table pagination properties
  const paginationOptions = {
    custom: true,
    totalSize: totalCount,
    sizePerPageList: uiHelpers.sizePerPageList,
    sizePerPage: redeemsUIProps.queryParams.pageSize,
    page: redeemsUIProps.queryParams.pageNumber,
  };
  return (
    <>
      <PaginationProvider pagination={paginationFactory(paginationOptions)}>
        {({ paginationProps, paginationTableProps }) => {
          return (
            <Pagination
              isLoading={listLoading}
              paginationProps={paginationProps}
            >
              <BootstrapTable
                wrapperClasses="table-responsive"
                bordered={false}
                classes="table table-head-custom table-vertical-center"
                bootstrap4
                remote
                keyField="_id"
                data={entities === null ? [] : entities}
                columns={columns}
                defaultSorted={uiHelpers.defaultSorted}
                onTableChange={getHandlerTableChange(
                  redeemsUIProps.setQueryParams
                )}
                selectRow={getSelectRow({
                  entities,
                  ids: redeemsUIProps.ids,
                  setIds: redeemsUIProps.setIds,
                })}
                {...paginationTableProps}
              >
                <PleaseWaitMessage entities={entities} />
                <NoRecordsFoundMessage entities={entities} />
              </BootstrapTable>
            </Pagination>
          );
        }}
      </PaginationProvider>
    </>
  );
}
